/**
 * Database Storage Service
 *
 * Persists databases (and their items). F 144: remote-backed, with localStorage
 * kept as an offline mirror.
 * Same pattern as pageStorage.ts.
 *
 * Storage key: `freshcards:databases`
 * Format: JSON-serialised map of databaseId → Database
 */

// F 144 — reads/writes go through RemoteState: an in-memory cache hydrated
// from the FCC state service and flushed back on a debounce. The localStorage
// mirror is kept in step, so behaviour is identical if the service is
// unreachable, and the signatures here stay SYNCHRONOUS.
import { RemoteState } from '../storage/RemoteState';
// F 452 — the browser's copy of the pages it has already read, and the bus
// that tells the app when the graph cannot be reached.
import { readGraphPage, writeGraphPage } from '../storage/graphPageCache';
import { eventBus } from '../event-bus';
import { graphFetch } from '../storage/graphAuth';

import type { Database, DatabaseItem } from '../types/models/Database';
import type { PropertyDefinition, PropertyValue } from '../types/models/Property';

const STORAGE_KEY = 'freshcards:databases';

type DatabaseMap = Record<string, Database>;

/**
 * Load all databases from storage.
 * Returns an empty object if nothing is stored or the stored data is corrupt.
 */
export function loadDatabases(): DatabaseMap {
  try {
    const data = RemoteState.get(STORAGE_KEY);
    if (!data) return {};
    const parsed = JSON.parse(data);
    if (typeof parsed !== 'object' || parsed === null) return {};
    // Re-hydrate Date fields (JSON.parse turns them into strings)
    // F 470 — `migrated` records whether this read actually CHANGED
    // anything, because a read that changes nothing must not write.
    let migrated = false;
    for (const db of Object.values(parsed) as Database[]) {
      db.createdAt = new Date(db.createdAt);
      db.updatedAt = new Date(db.updatedAt);
      // ⇒⇒⇒ F 499 — THERE IS NO LOAD-TIME VERSION SEED, AND THE FIRST
      // VERSION OF THIS FILE HAD ONE. ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
      // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THE FIRST VERSION SEEDED `db.version = 1` HERE AND SET
      // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THE `migrated` FLAG, TO PERSIST THE SEED. ⇒⇒⇒
      // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THAT MADE `loadDatabases()` WRITE
      // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ON EVERY MOUNT — AND F 497's GUARD SAYS A
      // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ MOUNT MUST NOT WRITE `freshcards:databases` BACK,
      // ⇒⇒□ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒□ BECAUSE THAT WRITE IS WHAT REPLACED THE LIVE MAP
      // ⇒⇒□ ⇒⇒□ OF SEVEN RECORDS WITH ONE. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SEED IS NOT NEEDED. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SERVER'S VERSION LIVES IN ITS OWN KEY
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ (`freshcards:__version__:<key>`), NOT IN THE
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ RECORD, so the client never has to MIGRATE
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ the stored records to make the guard work. ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND `saveDatabase()`
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ALREADY TREATS A RECORD
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WITH NO VERSION AS
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ VERSION 0, SO ITS FIRST
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WRITE MAKES IT VERSION 1.
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
      for (const item of db.items) {
        item.createdAt = new Date(item.createdAt);
        item.updatedAt = new Date(item.updatedAt);
        // Migration: replace any remote picsum.photos cover URLs with
        // inline CSS gradients (instant, offline, theme-aware). This
        // upgrades old seed data that used picsum URLs to the new
        // gradient-based covers without requiring a re-seed.
        const coverProp = item.properties.find(p => p.propertyId === 'cover');
        if (coverProp && typeof coverProp.value === 'string'
            && coverProp.value.startsWith('http')) {
          const grads = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
            'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
            'linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)',
            'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)',
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
          ];
          // Deterministic by item id hash so each card keeps the same gradient
          let h = 0;
          for (let i = 0; i < item.id.length; i++) h = (h * 31 + item.id.charCodeAt(i)) >>> 0;
          coverProp.value = grads[h % grads.length];
          db.updatedAt = new Date(); // mark db as updated
          migrated = true;
        }
      }
    }
    // F 470 — PERSIST THE MIGRATION ONLY IF IT CHANGED SOMETHING.
    //
    // This used to write on EVERY read, so every sidebar render marked the
    // whole map as pending and flushed it to the server. During a data
    // migration that is a live hazard: a browser that hydrated BEFORE the
    // migration holds the old map in its cache, and one read would PUT the
    // old map back and erase what was just moved. A read that changes
    // nothing must not write.
    if (migrated) {
      try { RemoteState.set(STORAGE_KEY, JSON.stringify(parsed)); } catch {}
    }
    return parsed;
  } catch (error) {
    console.error('[DatabaseStorage] Failed to load databases:', error);
    return {};
  }
}

/**
 * Save all databases to storage.
 *
 * F 497 — THIS MERGES. IT DOES NOT REPLACE.
 *
 * MEASURED on the live graph, 2026-10-28: the `freshcards:databases` map went
 * from SEVEN records to ONE. The single survivor was SYNTHETIC — its `name`
 * was its own id — which is what `createDatabase(id, title)` produces when
 * `loadDatabase(id)` returns undefined. And `loadDatabase` returns undefined
 * when the store has NOT been hydrated: the read returns `{}`, the surface
 * manufactures a record, and the write of that one record REPLACED the
 * server's map.
 *
 * The six records the reader never saw were deleted by a write that had no
 * business deleting anything. A writer that read an empty map has LEARNED
 * NOTHING about what the map contains, so it must not be able to remove any
 * of it.
 *
 * ⇒ THE WRITE IS A MERGE. A key that is present in the incoming map is
 *   written; a key that is ABSENT is left exactly as it is. A map that
 *   legitimately shrinks now needs an explicit delete (see `deleteDatabase`,
 *   which removes the key and writes the map back).
 *
 * The whole map is still ONE key in the graph, so the merge is computed
 * against the store's current value. That value is the server's map once
 * the store is hydrated; when it is not hydrated it is the empty map, and
 * the merge is then a no-op on the absent keys — which is exactly the
 * behaviour that stops the truncation. The records the reader never saw
 * are still on the server, and the server's copy of the key is not
 * overwritten with a map that lacks them.
 */
export function saveDatabases(databases: DatabaseMap): void {
  try {
    /**
     * F 497 — AND AN UNHYDRATED CLIENT MUST NOT WRITE THE MAP AT ALL.
     *
     * The merge above protects against a PARTIAL map: a key absent from the
     * incoming map is left alone. It cannot protect against an EMPTY one,
     * because the merge base is then empty too — the merge writes the one
     * record it has and the server's seven are gone.
     *
     * MEASURED on the FCC API (`/opt/fcc-api/server.js`): `PUT /kv` iterates
     * the payload and calls `graph.write(k, v)` per KEY. There is no
     * per-record merge on the server: the value of `freshcards:databases` is
     * REPLACED wholesale by whatever the client sends. So the client must
     * send the complete map, and a client that has not read the complete map
     * must send nothing.
     *
     * ⇒ THE GUARD IS THE HYDRATION, and it is checked against the SERVER
     *   being reachable:
     *
     *   - server reachable + hydration NOT complete ⇒ the client has not
     *     read the server's map. It must not write the map key. (This is the
     *     live scenario: the app opened a database before hydration finished.)
     *   - server NOT reachable ⇒ there is no server map to truncate. The
     *     local mirror is the only store, and writing it is correct and
     *     harmless. Blocking this would break the app in exactly the
     *     degraded mode it is supposed to survive.
     */
    if (RemoteState.isServerReachable() && !RemoteState.isHydrationComplete()) {
      console.warn(
        '[DatabaseStorage] F 497: the store is not hydrated yet — the database map ' +
        'was NOT written, because writing it would replace the server\'s map with ' +
        'a partial one. The write will happen on the next real mutation.',
      );
      return;
    }

    const current = loadDatabases();
    const merged: DatabaseMap = { ...current };
    for (const [id, db] of Object.entries(databases)) {
      merged[id] = db;
    }
    RemoteState.set(STORAGE_KEY, JSON.stringify(merged));
  } catch (error) {
    console.error('[DatabaseStorage] Failed to save databases:', error);
  }
}

/**
 * F 470 — THE COUNT RULE. One place, so a badge and a view can never
 * disagree about how many items a database has.
 *
 * A database whose items are PAGED carries no inline items; the record's
 * `itemPages.total` is the count. An inline database carries its items.
 */
export function databaseItemCount(db: Database | undefined | null): number {
  if (!db) return 0;
  if (Array.isArray(db.items) && db.items.length > 0) return db.items.length;
  if (db.itemPages && typeof db.itemPages.total === 'number') return db.itemPages.total;
  return 0;
}

/**
 * F 497 — WHERE A PAGED DATABASE'S PAGES LIVE.
 *
 * The record's own descriptor when it carries one; otherwise the house's
 * convention, `freshcards:db:<id>:items:`.
 *
 * MEASURED on the live graph: the HQ Backup record carries NO descriptor —
 * its keys are id, name, schema, items, createdAt, updatedAt, and its
 * `items` is 0 — while its 16,783 items sit under the convention prefix
 * (49 keys, p0000…p0048, 16,783 items, measured by reading it).
 *
 * ⇒ THE FALLBACK IS IN ONE PLACE. F 486 found this and worked around it
 *   inside the HQ reader; a second copy in a second reader is how two
 *   readers end up disagreeing about where the pages are.
 */
export function databaseItemPagesPrefix(db: Database | undefined | null): string | null {
  if (!db) return null;
  const declared = db.itemPages?.prefix;
  if (typeof declared === 'string' && declared.length > 0) return declared;
  return db.id ? `freshcards:db:${db.id}:items:` : null;
}

/**
 * F 497 — A PAGED ITEM, AS A `DatabaseItem`.
 *
 * A paged item is NOT a `DatabaseItem`. MEASURED, the HQ pages carry
 * `hq_id`, `hq_kind`, `level`, `body`, `chars`, a numeric `ts` and a NULL
 * `title` — and no `properties` at all.
 *
 * ⇒ THE NORMAL VIEW NEEDS A `DatabaseItem`. Its card renderer reads
 *   `item.title`, `item.properties.length` and `item.createdAt
 *   .toLocaleDateString()` — so an un-normalised item renders an EMPTY
 *   card, and a missing `properties` array crashes the text filter
 *   (`item.properties.some(...)`).
 *
 * ⇒ THE NORMALISER IS GENERIC. It knows nothing about HQ: it maps the
 *   fields every record shape has in common (id, title, body, timestamp)
 *   and defaults the rest, so an item that is already a `DatabaseItem`
 *   passes through unchanged.
 */
export function pagedItemToDatabaseItem(raw: unknown, databaseId = ''): DatabaseItem {
  const it = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const text = (v: unknown): string =>
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const body = text(it.body) || text(it.content) || text(it.text);
  const firstLine = body.split('\n').find((line) => line.trim().length > 0) ?? '';
  const title =
    text(it.title).trim() ||
    firstLine.trim().slice(0, 120) ||
    text(it.hq_id) ||
    text(it.id) ||
    'Item';
  /**
   * F 497 — THE TIMESTAMPS SURVIVE, whichever shape they arrive in.
   *
   * A paged record carries a numeric `ts`; an item that is ALREADY a
   * `DatabaseItem` carries `Date` objects. MEASURED: with only the number
   * case handled, a `DatabaseItem` came back with `createdAt` set to
   * "now" — its own timestamp silently replaced, which is how a card ends
   * up dated to the day it was read.
   */
  const stampOf = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (v instanceof Date) {
      const t = v.getTime();
      return Number.isNaN(t) ? null : t;
    }
    if (typeof v === 'string' && v.length > 0) {
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? null : t;
    }
    return null;
  };
  const stamp =
    stampOf(it.ts) ??
    stampOf(it.createdAt) ??
    stampOf(it.updatedAt) ??
    Date.now();
  const createdAt = new Date(stampOf(it.createdAt) ?? stamp);
  const updatedAt = new Date(stampOf(it.updatedAt) ?? stamp);
  const tags = Array.isArray(it.tags)
    ? (it.tags as string[])
    : [text(it.hq_kind), text(it.level)].filter((t) => t.length > 0);
  return {
    id: text(it.id) || text(it.hq_id) || `paged-${stamp}`,
    databaseId: text(it.databaseId) || text(it.db) || databaseId,
    title,
    properties: Array.isArray(it.properties) ? (it.properties as PropertyValue[]) : [],
    tags,
    createdAt,
    updatedAt,
  };
}

/**
 * F 497 — READ THE FIRST PAGE OF A PAGED DATABASE, AS `DatabaseItem`s.
 *
 * The read is PAGED (`limit`/`offset`), because the whole prefix is
 * 17.8 MB for the HQ namespace and a surface must never pull that to
 * render a window of it (F 486).
 *
 * ⇒ THE PREFIX COMES FROM `databaseItemPagesPrefix`, so a record that
 *   lost its descriptor still resolves — the pages are where the house
 *   lays them out, and the pointer on the record is a convenience, not
 *   the only way there.
 */
export async function loadPagedDatabaseItems(
  db: Database,
  opts?: { limit?: number; offset?: number },
): Promise<{
  items: DatabaseItem[];
  total?: number;
  nextOffset?: number;
  degraded: boolean;
  skipped: number;
  /** F 452 — this page came from the browser's cache, not from the graph. */
  fromCache?: boolean;
  /** F 452 — when the cached page was written. */
  cachedAt?: number;
}> {
  const prefix = databaseItemPagesPrefix(db);
  if (!prefix) return { items: [], degraded: false, skipped: 0 };
  const res = await loadDatabaseItemPages(db, { ...opts, prefix });
  return {
    items: res.items.map((raw) => pagedItemToDatabaseItem(raw, db.id)),
    total: res.total,
    nextOffset: res.nextOffset,
    degraded: res.degraded,
    skipped: res.skipped,
    fromCache: res.fromCache,
    cachedAt: res.cachedAt,
  };
}

/**
 * F 470 — LOAD THE ITEMS OF A PAGED DATABASE.
 *
 * The pages ARE the items; the record names them. One prefix read fetches
 * them, because the FC adapter has no per-key GET (measured) and a narrow
 * prefix read costs the same as a wide one (measured: 1.7 s either way —
 * every read is a full store scan), so paging the READ would only add
 * round trips without making the response smaller.
 *
 * F 486 — THE READ IS PAGED NOW. The server can answer with a page
 * (`limit`/`offset`), so the client no longer pulls 17.8 MB to render a
 * window of it. The comment above was true when every read was a full store
 * scan; the response size is now bounded by the page, not by the store.
 */
export async function loadDatabaseItemPages(db: Database, opts?: { limit?: number; offset?: number; prefix?: string }): Promise<{
  items: unknown[];
  skipped: number;
  pageCount: number;
  degraded: boolean;
  /**
   * F 486 — the TOTAL number of ITEMS under the prefix, as the server counted
   * them. Only meaningful when the read was paged; `pageCount` is how many
   * KEYS this call actually read.
   */
  total?: number;
  /**
   * F 486 — the offset of the NEXT page, in items. The client must use this
   * and not its own item count: it skips some items the server does not, and
   * a drifting offset would replay items the user already has.
   */
  nextOffset?: number;
  /**
   * F 452 — THIS PAGE CAME FROM THE BROWSER'S CACHE, not from the graph.
   *
   * The read failed (the network is gone) and this browser had already read
   * this exact page. The items are real — they were read from the graph
   * earlier — but they are as old as the cache entry, and the surface that
   * shows them must say so rather than pretending the graph answered.
   */
  fromCache?: boolean;
  /** When the cached page was written. */
  cachedAt?: number;
}> {
  const pages = db.itemPages;
  /**
   * The prefix the record names — or one the caller names outright. F 486:
   * the HQ record lost its descriptor, and the caller knows where the pages
   * are even when the record does not.
   */
  const prefix = opts?.prefix ?? pages?.prefix;
  if (!prefix) return { items: [], skipped: 0, pageCount: 0, degraded: false };
  const limit = typeof opts?.limit === 'number' ? opts.limit : 0;
  const offset = typeof opts?.offset === 'number' ? opts.offset : 0;
  let values: Record<string, string> | null = null;
  let total: number | undefined;
  let nextOffset: number | undefined;
  try {
    // F 486 — READ A PAGE. The whole-prefix read is 17.8 MB for the HQ
    // namespace; `limit`/`offset` ask the server for one page instead.
    const query = new URLSearchParams({ prefix });
    if (typeof opts?.limit === 'number') query.set('limit', String(opts.limit));
    if (typeof opts?.offset === 'number') query.set('offset', String(opts.offset));
    const res = await graphFetch('/api/fcc', `/kv?${query.toString()}`);
    if (!res || !res.ok) {
      // F 452 — THE NETWORK IS GONE. Fall back to the page this browser has
      // already read, if it has it.
      return await readCachedPage(prefix, limit, offset);
    }
    const body = (await res.json()) as {
      ok?: boolean; values?: Record<string, string>; total?: number; nextOffset?: number;
    };
    if (!body || body.ok !== true || !body.values) {
      return await readCachedPage(prefix, limit, offset);
    }
    values = body.values;
    if (typeof body.total === 'number') total = body.total;
    if (typeof body.nextOffset === 'number') nextOffset = body.nextOffset;
    // F 452 — REMEMBER THE PAGE. The app can now show this page with the
    // network unplugged. Fire-and-forget: a cache that cannot be written is
    // a cache miss later, never a failed read now.
    void writeGraphPage(prefix, offset, limit, values, { total, nextOffset });
    eventBus.emit('graph:reachable', { prefix, offset, limit });
  } catch {
    return await readCachedPage(prefix, limit, offset);
  }
  const parsed = parsePageItems(values, prefix);
  return {
    items: parsed.items,
    skipped: parsed.skipped,
    pageCount: Object.keys(values).length,
    degraded: false,
    total,
    nextOffset,
  };
}

/**
 * F 452 — THE ITEMS IN A PAGE'S KEY/VALUE PAIRS.
 *
 * A page is a set of keys, and each key's value is a JSON array of items (or
 * a single item). The same parsing is used for a live page and a cached one,
 * so the two can never disagree about what a page contains.
 */
function parsePageItems(
  values: Record<string, string>,
  prefix: string,
): { items: unknown[]; skipped: number } {
  const items: unknown[] = [];
  let skipped = 0;
  for (const [key, raw] of Object.entries(values)) {
    if (!key.startsWith(prefix)) { skipped += 1; continue; }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) items.push(...parsed);
      else if (parsed && typeof parsed === 'object') items.push(parsed);
      else skipped += 1;
    } catch {
      skipped += 1;
    }
  }
  return { items, skipped };
}

/**
 * F 452 — THE PAGE THIS BROWSER ALREADY READ.
 *
 * Called only after a live read has failed. It answers with the cached page
 * when there is one, and with an empty degraded read when there is not — the
 * two are different facts, and the surface must be able to tell them apart.
 */
async function readCachedPage(
  prefix: string,
  limit: number,
  offset: number,
): Promise<{
  items: unknown[];
  skipped: number;
  pageCount: number;
  degraded: boolean;
  total?: number;
  nextOffset?: number;
  fromCache?: boolean;
  cachedAt?: number;
}> {
  const page = await readGraphPage(prefix, offset, limit);
  if (!page) {
    eventBus.emit('graph:unreachable', { prefix, offset, limit, cached: false });
    return { items: [], skipped: 0, pageCount: 0, degraded: true };
  }
  const parsed = parsePageItems(page.values, prefix);
  eventBus.emit('graph:unreachable', { prefix, offset, limit, cached: true });
  return {
    items: parsed.items,
    skipped: parsed.skipped,
    pageCount: Object.keys(page.values).length,
    degraded: true,
    total: page.total,
    nextOffset: page.nextOffset,
    fromCache: true,
    cachedAt: page.cachedAt,
  };
}

/**
 * Load a single database by id.
 * Returns undefined if not found.
 */
export function loadDatabase(databaseId: string): Database | undefined {
  const all = loadDatabases();
  return all[databaseId];
}

/**
 * Save a single database. Other databases are preserved.
 *
 * F 497 — A PAGED DATABASE'S DESCRIPTOR SURVIVES A WRITE THAT DID NOT MEAN TO
 * TOUCH IT.
 *
 * MEASURED on the live graph: the HQ Backup record carried NO paging
 * descriptor while its 16,783 items sat under `freshcards:db:<id>:items:`.
 * The mechanism that lost it is THIS function: it replaces one record with
 * the object it is handed, so a writer holding a copy from before the
 * migration — or a surface that simply re-saves what it read — drops a
 * field it never knew about. And the descriptor is the ONLY pointer to
 * those pages (the pre-migration manifest key is gone: measured, 0 keys),
 * so losing it is losing the database: the sidebar read "HQ Backup 0" and
 * the surface rendered nothing.
 *
 * ⇒ THE DESCRIPTOR IS PRESERVED, NOT MERGED. A caller that means to change
 *   the pages passes its own descriptor; a caller that does not know about
 *   them keeps the ones that are there.
 */
export function saveDatabase(database: Database): void {
  const all = loadDatabases();
  const existing = all[database.id];
  all[database.id] = {
    ...database,
    itemPages: database.itemPages ?? existing?.itemPages,
    // F 499 — THE RECORD'S VERSION IS INCREMENTED ON EVERY WRITE.
    //
    // ⇒⇒⇒ A record with no version is version 0, so its first write makes
    // it version 1. ⇒⇒⇒ A record that already has one is incremented. ⇒⇒⇒
    // The increment is what makes the server's stale-write guard meaningful:
    // the client says "I am writing version N+1", and the server refuses a
    // version OLDER than the one it holds.
    version: (database.version ?? existing?.version ?? 0) + 1,
    updatedAt: new Date(),
  };
  saveDatabases(all);
}

/**
 * Delete a database by id.
 *
 * F 497 — `saveDatabases` MERGES, so a delete can no longer be expressed as
 * "remove the key and write the map back": the merge would put the key
 * back. A delete is therefore an EXPLICIT removal, and it is the ONLY place
 * a key is removed from the map.
 *
 * The write is still a full-map write of the current map minus the deleted
 * key, because the map is ONE key in the graph: there is no per-record
 * delete at the KV layer.
 */
export function deleteDatabase(databaseId: string): void {
  const all = loadDatabases();
  if (!(databaseId in all)) return;
  delete all[databaseId];
  try {
    RemoteState.set(STORAGE_KEY, JSON.stringify(all));
  } catch (error) {
    console.error('[DatabaseStorage] Failed to delete database:', error);
  }
}

/**
 * Create a fresh database with sensible defaults:
 * - Title property
 * - Date property (for date grouping)
 * - Status property (Active / Completed)
 * - An empty items list
 *
 * Useful for the "New Database" quick action.
 */
export function createDefaultDatabase(id: string, name: string = 'Untitled Database'): Database {
  const now = new Date();
  const titleProp: PropertyDefinition = {
    id: 'title',
    name: 'Title',
    type: 'title',
    visible: true,
    position: 0,
  };
  const dateProp: PropertyDefinition = {
    id: 'date',
    name: 'Date',
    type: 'date',
    visible: true,
    position: 1,
    dateFormat: 'YYYY-MM-DD',
  };
  const statusProp: PropertyDefinition = {
    id: 'status',
    name: 'Status',
    type: 'status',
    visible: true,
    position: 2,
    statusOptions: [
      { id: 'status_active', name: 'Active', color: '#52b788', state: 'active' },
      { id: 'status_completed', name: 'Completed', color: '#adb5bd', state: 'completed' },
    ],
  };
  const tagProp: PropertyDefinition = {
    id: 'tag',
    name: 'Tag',
    type: 'multi_select',
    visible: true,
    position: 3,
    options: [
      { id: 'tag_freshvibe', name: 'FRESHVIBE', color: '#c4b5fd' },
    ],
  };
  const checkboxProp: PropertyDefinition = {
    id: 'finished',
    name: 'Finished',
    type: 'checkbox',
    visible: true,
    position: 4,
  };
  return {
    id,
    name,
    schema: {
      id: `${id}_schema`,
      properties: [titleProp, dateProp, statusProp, tagProp, checkboxProp],
    },
    views: [
      { id: `${id}_kanban`, name: 'Board', type: 'kanban' },
      { id: `${id}_list`, name: 'List', type: 'list' },
    ],
    items: [],
    createdAt: now,
    updatedAt: now,
  };
}

export default {
  loadDatabases,
  saveDatabases,
  databaseItemCount,
  databaseItemPagesPrefix,
  pagedItemToDatabaseItem,
  loadDatabaseItemPages,
  loadPagedDatabaseItems,
  loadDatabase,
  saveDatabase,
  deleteDatabase,
  createDefaultDatabase,
};
